// src/onus_components/WanModal.jsx — la conexión PPPoE de la ONT
//
// Primero carga lo que la app sabe del cliente (usuario PPPoE, VLAN del
// plan, perfil de política según el modelo) y deja corregirlo antes de
// mandar. El backend borra la WAN anterior y la vuelve a crear.

import React, { useEffect, useMemo, useState } from "react";
import { authFetch } from "../utils/authFetch";
import { Iconos } from "../componentes/base";
import ModalPasos, { fichaOnu, leerRespuesta, ubicacionDe } from "../componentes/olt/ModalPasos";

export default function WanModal({ open, onu, onClose, onSuccess }) {
  const [cargando, setCargando] = useState(false);
  const [errorCarga, setErrorCarga] = useState("");
  const [preview, setPreview] = useState(null);

  const [usuario, setUsuario] = useState("");
  const [clave, setClave] = useState("");
  const [vlan, setVlan] = useState(0);
  const [ipIndex, setIpIndex] = useState(0);
  const [perfilWan, setPerfilWan] = useState(0);
  const [http, setHttp] = useState(true);

  const [estado, setEstado] = useState("reposo");
  const [salida, setSalida] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !onu?.id) return undefined;
    let cancelado = false;
    setEstado("reposo"); setSalida([]); setError("");
    setPreview(null); setErrorCarga(""); setCargando(true);

    authFetch(`/api/onus/${onu.id}/wan_preview`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.ok) throw new Error(data?.mensaje || data?.detalle || "No se pudo cargar la WAN.");
        if (cancelado) return;
        const d = data.defaults || {};
        setPreview(data);
        setIpIndex(Number(d.ip_index ?? 0));
        setPerfilWan(Number(d.wan_profile_id ?? 0));
        setHttp(Boolean(d.enable_http ?? true));
        setUsuario(data?.cliente?.pppoe_user || data?.pppoe_user || "");
        setClave(data?.cliente?.pppoe_pass || data?.pppoe_pass || "");
        setVlan(Number(data?.plan_info?.vlan ?? data?.vlan ?? 0));
      })
      .catch((e) => { if (!cancelado) setErrorCarga(String(e?.message || e)); })
      .finally(() => { if (!cancelado) setCargando(false); });

    return () => { cancelado = true; };
  }, [open, onu?.id]);

  const info = preview?.onu || {};
  const u = ubicacionDe({ ...onu, ...info });
  const politica = preview?.policy_profile || {};
  const opcionesIp = preview?.defaults?.ip_index_options || [0, 1, 2, 3];
  const listo = Boolean(preview && usuario && clave && vlan);

  // Las fases siguen el orden de `wan_apply` en el backend. El acceso web
  // solo entra si está pedido: poner sus fases sin mandarlas las
  // dejaría marcadas como hechas sin haber pasado.
  const fases = useMemo(() => {
    const p = `${u.pon} ${u.ont}`;
    const lista = [
      { rotulo: "Entrar al puerto", coincide: /^interface gpon/, cmds: [`interface gpon ${u.frameSlot}`] },
      { rotulo: "Quitar la WAN anterior", coincide: /^undo ont/, cmds: [
        `undo ont wan-config ${p} ip-index ${ipIndex}`, `undo ont internet-config ${p}`,
        `undo ont policy-route-config ${p}`, `undo ont ipconfig ${p} ip-index ${ipIndex}`] },
      { rotulo: "Crear el PPPoE", coincide: /^ont ipconfig/, cmds: [
        `ont ipconfig ${p} ip-index ${ipIndex} pppoe user-account username ${usuario || "…"} password •••••••• vlan ${vlan || "…"} priority 0`] },
      { rotulo: "Enganchar WAN y rutas", coincide: /^ont (internet-config|wan-config|policy-route-config|port route)/, cmds: [
        `ont internet-config ${p} ip-index ${ipIndex}`, `ont wan-config ${p} ip-index ${ipIndex} profile-id ${perfilWan}`,
        `ont policy-route-config ${p} profile-id ${politica.id ?? "…"}`, `ont port route ${p} eth … enable`] },
      { rotulo: "Comprobar", coincide: /^display ont/, cmds: [`display ont ipconfig ${p}`, `display ont wan-config ${p}`] },
      { rotulo: "Salir del puerto", coincide: /^quit$/, cmds: ["quit"] },
    ];
    if (http) {
      lista.push(
        { rotulo: "Abrir el acceso web", coincide: /^(diagnose|ont wan-access)/, cmds: ["diagnose", `ont wan-access http ${u.fsp} ${u.ont} enable`] },
        { rotulo: "Salir de diagnose", coincide: /^quit$/, cmds: ["quit"] },
      );
    }
    return lista;
  }, [u.pon, u.ont, u.frameSlot, u.fsp, ipIndex, usuario, vlan, perfilWan, politica.id, http]);

  async function ejecutar() {
    if (!onu?.id || !listo) return;
    setEstado("corriendo"); setSalida([]); setError("");
    try {
      const res = await authFetch(`/api/onus/${onu.id}/wan_apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ip_index: ipIndex, enable_http: http, wan_profile_id: perfilWan,
          pppoe_user: usuario, pppoe_pass: clave, vlan: Number(vlan),
        }),
      });
      const data = await leerRespuesta(res);
      setSalida(Array.isArray(data?.outputs) ? data.outputs : []);
      if (!res.ok || !data?.ok) throw new Error(data?.mensaje || data?.detalle || "No se pudo aplicar la WAN.");
      onSuccess?.(data?.mensaje || "WAN aplicada");
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setEstado("fin");
    }
  }

  const deshabilitado = cargando || !preview;

  return (
    <ModalPasos
      abierto={open}
      titulo="Configurar la WAN"
      icono={Iconos.olt}
      ficha={fichaOnu({ ...onu, ...info })}
      aviso="Rehace la conexión PPPoE con los datos de abajo. Primero se borra la WAN anterior, así que el cliente pierde internet unos segundos mientras vuelve a marcar."
      verbo="Aplicar WAN"
      listo={listo}
      estado={estado}
      salida={salida}
      error={error}
      mensaje={`WAN aplicada. La ONT marca PPPoE como ${usuario} por la VLAN ${vlan}.`}
      fases={fases}
      onEjecutar={ejecutar}
      onCerrar={onClose}
    >
      {cargando && (
        <p className="mp-cargando"><Iconos.cargando size={15} strokeWidth={2.2} className="mp-gira" /> Cargando los datos del cliente…</p>
      )}
      {errorCarga && (
        <div className="mp-aviso mp-aviso--peligro"><Iconos.error size={16} strokeWidth={2} /><span>{errorCarga}</span></div>
      )}
      {!cargando && preview && (
        <div className="mp-campos">
          <label className="mp-campo">
            <span>Usuario PPPoE</span>
            <input className="mono" value={usuario} onChange={(e) => setUsuario(e.target.value)} disabled={deshabilitado} />
          </label>
          <label className="mp-campo">
            <span>Contraseña PPPoE</span>
            <input className="mono" value={clave} onChange={(e) => setClave(e.target.value)} disabled={deshabilitado} />
          </label>
          <label className="mp-campo">
            <span>VLAN</span>
            <input type="number" value={vlan} onChange={(e) => setVlan(Number(e.target.value || 0))} disabled={deshabilitado} />
            <small>Plan {preview?.plan_info?.nombre || preview?.cliente?.plan || "—"}</small>
          </label>
          <label className="mp-campo">
            <span>ip-index</span>
            <select value={ipIndex} onChange={(e) => setIpIndex(Number(e.target.value))} disabled={deshabilitado}>
              {opcionesIp.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="mp-campo">
            <span>Perfil WAN</span>
            <input type="number" value={perfilWan} onChange={(e) => setPerfilWan(Number(e.target.value || 0))} disabled={deshabilitado} />
            <small>Normalmente 0 (route-nat-enabled)</small>
          </label>
          <label className="mp-campo">
            <span>Perfil de política</span>
            <input value={`${politica.id ?? "—"} · ${politica.profile_name ?? ""}`} disabled />
            <small>Según el modelo de la ONT</small>
          </label>
          <label className="mp-campo">
            <span>Acceso web</span>
            <select value={http ? "1" : "0"} onChange={(e) => setHttp(e.target.value === "1")} disabled={deshabilitado}>
              <option value="1">Abrirlo</option>
              <option value="0">No tocarlo</option>
            </select>
          </label>
        </div>
      )}
    </ModalPasos>
  );
}
